import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createConsumption, getConsumptionsByBuildingId } from '../../Fetch/fetchElectricVehicleConsumption';
import { getListUsers } from '../../Fetch/fetchUser';
import { ChevronDown } from 'lucide-react';
import styles from './ElectricVehicleForm.module.css';

const ElectricVehicleCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [buildingId, setBuildingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [tenantSearchTerm, setTenantSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    counter: '',
    previousCounter: '', // מונה חודש קודם - יטען אוטומטית
    powerConsumption: '', // יטען מהשורה האחרונה או 0
    parkingNumber: '',
    phoneNumber: '',
    totalPowerConsumptionPerMonth: '', // כמות הקילוואט שצרכו
    totalMoneyNeedPay: 0, // יחושב אוטומטית
    dateCharged: null,
    totalMoneyPaid: 0,
    apartmentNumber: '',
    userID: '',
    userName: '',
    buildingID: null
  });

  // Get buildingId from localStorage and fetch users
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.buildingId) {
      setBuildingId(user.buildingId);
      setFormData(prev => ({
        ...prev,
        buildingID: user.buildingId
      }));
      fetchUsers(user.buildingId);
    } else {
      setError('לא נמצא מזהה בניין. אנא התחבר מחדש.');
    }
  }, []);

  // Fetch users for the building - filter only users with charging station
  const fetchUsers = async (bldgId) => {
    setIsLoadingUsers(true);
    try {
      const data = await getListUsers(bldgId);
      // Filter only users who have charging station = true
      const usersWithChargingStation = data.filter(user => user.chargingStation === true);
      setUsers(usersWithChargingStation);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Fetch last PowerConsumption value for this building
  useEffect(() => {
    const fetchLastPrice = async () => {
      if (!buildingId) return;

      try {
        const data = await getConsumptionsByBuildingId(buildingId);
        if (data && data.length > 0) {
          // Get the last record's PowerConsumption (data is already ordered by date descending from API)
          const lastRecord = data[0];
          setFormData(prev => ({
            ...prev,
            powerConsumption: lastRecord.powerConsumption.toString()
          }));
        } else {
          // No records exist, set to 0
          setFormData(prev => ({
            ...prev,
            powerConsumption: '0'
          }));
        }
      } catch (err) {
        // If error, default to 0
        setFormData(prev => ({
          ...prev,
          powerConsumption: '0'
        }));
      }
    };

    fetchLastPrice();
  }, [buildingId]);

  // חישוב אוטומטי של totalMoneyNeedPay - (מונה נוכחי - מונה קודם) × מחיר לקילוואט
  useEffect(() => {
    const counter = parseFloat(formData.counter) || 0;
    const previousCounter = parseFloat(formData.previousCounter);
    const powerConsumption = parseFloat(formData.powerConsumption) || 0;

    // אם אין מונה קודם (ריק או 0), נקח רק את המונה הנוכחי
    let consumption;
    if (!previousCounter || previousCounter === '' || previousCounter === 0) {
      consumption = counter;
    } else {
      // אם יש מונה קודם, נחשב: מונה נוכחי - מונה קודם
      consumption = counter - parseFloat(previousCounter);
    }

    const calculatedAmount = consumption * powerConsumption;

    setFormData(prev => ({
      ...prev,
      totalPowerConsumptionPerMonth: consumption,
      totalMoneyNeedPay: calculatedAmount
    }));
  }, [formData.counter, formData.previousCounter, formData.powerConsumption]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!tenantSearchTerm) return true;
    const searchLower = tenantSearchTerm.toLowerCase();
    return (
      user.fullName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.apartmentNumber?.toString().includes(tenantSearchTerm)
    );
  }).sort((a, b) => {
    const apartmentA = parseInt(a.apartmentNumber) || 0;
    const apartmentB = parseInt(b.apartmentNumber) || 0;
    return apartmentA - apartmentB;
  });

  const handleTenantDropdownToggle = () => {
    setIsTenantDropdownOpen(!isTenantDropdownOpen);
    if (!isTenantDropdownOpen) {
      setTenantSearchTerm('');
    }
  };

  const handleTenantSelection = async (tenant) => {
    setSelectedTenant(tenant);
    setFormData(prev => ({
      ...prev,
      apartmentNumber: tenant.apartmentNumber || '',
      userName: tenant.fullName || '',
      phoneNumber: tenant.phone || '',
      userID: tenant.userID || tenant.userId || tenant.id || '',
      parkingNumber: tenant.parkingNumber || ''
    }));
    setIsTenantDropdownOpen(false);
    setTenantSearchTerm('');

    // טעינת המונה האחרון של המשתמש הזה
    try {
      const data = await getConsumptionsByBuildingId(buildingId);
      // סינון רשומות של המשתמש הנוכחי בלבד
      const userConsumptions = data.filter(c =>
        c.userID === (tenant.userID || tenant.userId || tenant.id)
      );

      if (userConsumptions && userConsumptions.length > 0) {
        // לקחת את הרשומה האחרונה (המונה האחרון)
        const lastConsumption = userConsumptions[0];
        setFormData(prev => ({
          ...prev,
          previousCounter: lastConsumption.counter.toString()
        }));
      } else {
        // אם אין רשומות קודמות, המונה הקודם הוא 0
        setFormData(prev => ({
          ...prev,
          previousCounter: '0'
        }));
      }
    } catch (err) {
      console.error('Error loading previous counter:', err);
      setFormData(prev => ({
        ...prev,
        previousCounter: '0'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // בדיקה שיש buildingId
      if (!buildingId) {
        setError('לא נמצא מזהה בניין. אנא התחבר מחדש.');
        return;
      }

      // המרת הערכים למספרים והמרה לפורמט PascalCase
      // For parkingNumber: if it contains comma, take first number, otherwise parse the whole string
      const parkingNumberValue = formData.parkingNumber.includes(',')
        ? parseInt(formData.parkingNumber.split(',')[0].trim())
        : parseInt(formData.parkingNumber);

      const dataToSubmit = {
        Date: formData.date,
        Counter: parseFloat(formData.counter) || 0,
        PowerConsumption: parseFloat(formData.powerConsumption) || 0,
        ParkingNumber: parkingNumberValue || 0,
        PhoneNumber: formData.phoneNumber || '',
        TotalPowerConsumptionPerMonth: parseFloat(formData.totalPowerConsumptionPerMonth) || 0,
        TotalMoneyNeedPay: parseFloat(formData.totalMoneyNeedPay) || 0,
        DateCharged: formData.dateCharged && formData.dateCharged !== '' ? formData.dateCharged : null,
        TotalMoneyPaid: parseFloat(formData.totalMoneyPaid) || 0,
        BuildingID: buildingId,
        ApartmentNumber: parseInt(formData.apartmentNumber) || 0,
        UserID: parseInt(formData.userID) || 0,
        UserName: formData.userName || ''
      };

      console.log('Data to submit:', dataToSubmit);
      await createConsumption(dataToSubmit);
      navigate('/app/electric-vehicle');
    } catch (err) {
      console.error('Error creating consumption:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/app/electric-vehicle');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>הוספת צריכה</h1>
        <button className={styles.backButton} onClick={handleCancel}>
          חזור לרשימה
        </button>
      </div>

      {error && (
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      )}

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* פרטים כלליים */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>פרטים כלליים</h2>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>בחר דייר: <span className={styles.required}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#fff'
                    }}
                    onClick={handleTenantDropdownToggle}
                  >
                    <span>
                      {selectedTenant ?
                        `${selectedTenant.fullName} - דירה ${selectedTenant.apartmentNumber}` :
                        'בחר דייר...'
                      }
                    </span>
                    <ChevronDown size={20} />
                  </div>

                  {isTenantDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      marginTop: '4px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ padding: '0.5rem' }}>
                        <input
                          type="text"
                          value={tenantSearchTerm}
                          onChange={(e) => setTenantSearchTerm(e.target.value)}
                          placeholder="חפש דייר..."
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div>
                        {isLoadingUsers ? (
                          <div style={{ padding: '1rem', textAlign: 'center' }}>טוען דיירים...</div>
                        ) : filteredUsers.length === 0 ? (
                          <div style={{ padding: '1rem', textAlign: 'center' }}>לא נמצאו דיירים</div>
                        ) : (
                          filteredUsers.map(user => (
                            <div
                              key={user.userID || user.userId || user.id}
                              style={{
                                padding: '0.75rem',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                transition: 'background-color 0.2s'
                              }}
                              onClick={() => handleTenantSelection(user)}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <div style={{ fontWeight: 'bold' }}>{user.fullName}</div>
                              <div style={{ fontSize: '0.875rem', color: '#666' }}>
                                דירה {user.apartmentNumber}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>מספר דירה: <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  name="apartmentNumber"
                  value={formData.apartmentNumber}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>שם: <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  name="userName"
                  value={formData.userName}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>טלפון: <span className={styles.required}>*</span></label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>תאריך: <span className={styles.required}>*</span></label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>מספר חניה: <span className={styles.required}>*</span></label>
                <input
                  type="text"
                  name="parkingNumber"
                  value={formData.parkingNumber}
                  onChange={handleInputChange}
                  placeholder="1"
                  required
                />
                {formData.parkingNumber && formData.parkingNumber.includes(',') && (
                  <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    נמצאו מספר חניות - ניתן לערוך ולהשאיר רק את החניה הרלוונטית
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* צריכת חשמל וחיוב */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>צריכת חשמל וחיוב</h2>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>מונה חודש קודם (kWh):</label>
                <input
                  type="number"
                  step="0.01"
                  name="previousCounter"
                  value={formData.previousCounter}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  style={{ backgroundColor: '#f5f5f5' }}
                />
                <small>קריאת המונה מהחודש הקודם</small>
              </div>

              <div className={styles.formGroup}>
                <label>מונה נוכחי (kWh): <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  step="0.01"
                  name="counter"
                  value={formData.counter}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  required
                />
                <small>קריאת המונה החדשה</small>
              </div>

              <div className={styles.formGroup}>
                <label>מחיר לקילוואט (₪): <span className={styles.required}>*</span></label>
                <input
                  type="number"
                  step="0.01"
                  name="powerConsumption"
                  value={formData.powerConsumption}
                  onChange={handleInputChange}
                  placeholder="0.58"
                  required
                />
                <small>התעריף הנוכחי של חברת החשמל</small>
              </div>

              <div className={styles.formGroup}>
                <label>סה"כ צריכה (kWh):</label>
                <input
                  type="number"
                  step="0.01"
                  name="totalPowerConsumptionPerMonth"
                  value={formData.totalPowerConsumptionPerMonth}
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                  placeholder="0.00"
                />
                <small>מונה נוכחי - מונה קודם</small>
              </div>
            </div>

            <div className={styles.calculationBox}>
              <div className={styles.calculationItem}>
                <span>מונה נוכחי:</span>
                <strong>{parseFloat(formData.counter || 0).toFixed(2)}</strong>
              </div>
              <div className={styles.calculationSymbol}>-</div>
              <div className={styles.calculationItem}>
                <span>מונה קודם:</span>
                <strong>{parseFloat(formData.previousCounter || 0).toFixed(2)}</strong>
              </div>
              <div className={styles.calculationSymbol}>=</div>
              <div className={styles.calculationItem}>
                <span>צריכה:</span>
                <strong>{parseFloat(formData.totalPowerConsumptionPerMonth || 0).toFixed(2)} kWh</strong>
              </div>
              <div className={styles.calculationSymbol}>×</div>
              <div className={styles.calculationItem}>
                <span>מחיר לקילוואט:</span>
                <strong>₪{parseFloat(formData.powerConsumption || 0).toFixed(2)}</strong>
              </div>
              <div className={styles.calculationSymbol}>=</div>
              <div className={styles.calculationResult}>
                <span>סה"כ לתשלום:</span>
                <strong>₪{formData.totalMoneyNeedPay.toFixed(2)}</strong>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>סכום ששולם (₪):</label>
                <input
                  type="number"
                  step="0.01"
                  name="totalMoneyPaid"
                  value={formData.totalMoneyPaid}
                  onChange={handleInputChange}
                  placeholder="0.00"
                />
                <small>סכום שכבר שולם (אופציונלי)</small>
              </div>

              <div className={styles.formGroup}>
                <label>תאריך חיוב:</label>
                <input
                  type="date"
                  name="dateCharged"
                  value={formData.dateCharged || ''}
                  onChange={handleInputChange}
                />
                <small>תאריך החיוב בפועל (אופציונלי)</small>
              </div>

              <div className={styles.formGroup}>
                <div className={styles.balanceBox}>
                  <span>
                    {(formData.totalMoneyNeedPay - formData.totalMoneyPaid) > 0
                      ? 'יתרה לתשלום:'
                      : 'החזר לדייר:'}
                  </span>
                  <strong className={
                    (formData.totalMoneyNeedPay - formData.totalMoneyPaid) > 0
                      ? styles.unpaid
                      : styles.paid
                  }>
                    ₪{Math.abs(formData.totalMoneyNeedPay - formData.totalMoneyPaid).toFixed(2)}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          {/* כפתורי פעולה */}
          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'שומר...' : 'שמור רשומה'}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={loading}
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ElectricVehicleCreate;
